import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { KeenIcon } from '@/components';
import { triggerDownload } from '@/utils';
import eSignatureService from '@/services/e-signature/eSignatureService';
import { formatDueDate, isPastDue, recipientStatus } from './esignTracking';

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/**
 * OL-205: one document sent to many contacts — who signed, who hasn't, and
 * reminders for the ones still waiting.
 */
const ESignBatchModal = ({ open, batchId, onClose, onChanged }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null); // id of the row (or 'all') being reminded

  const load = useCallback(async () => {
    if (!batchId) return;
    setLoading(true);
    try {
      const response = await eSignatureService.getBatch(batchId);
      setData(response?.data || null);
    } catch (error) {
      toast.error(error?.error || error?.message || 'Could not load this group send.');
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  useEffect(() => {
    if (open) load();
    else setData(null);
  }, [open, load]);

  const remind = async (docId) => {
    setBusy(docId);
    try {
      const response = await eSignatureService.remindDocument(docId);
      toast.success(response?.message || 'Reminder sent.');
      await load();
      onChanged?.();
    } catch (error) {
      toast.error(error?.error || error?.message || 'Could not send the reminder.');
    } finally {
      setBusy(null);
    }
  };

  const remindAll = async () => {
    setBusy('all');
    try {
      const response = await eSignatureService.remindBatch(batchId);
      toast.success(response?.message || 'Reminders are on their way.');
      await load();
      onChanged?.();
    } catch (error) {
      toast.error(error?.error || error?.message || 'Could not send the reminders.');
    } finally {
      setBusy(null);
    }
  };

  const download = async (doc) => {
    try {
      const blob = await eSignatureService.downloadSignDocument(doc.id);
      triggerDownload(new Blob([blob], { type: 'application/pdf' }), `${doc.recipient?.name || 'Signed'} - ${data?.batch?.document_name || 'document.pdf'}`);
    } catch {
      toast.error('Could not download the signed copy.');
    }
  };

  if (!open) return null;

  const batch = data?.batch;
  const summary = data?.summary;
  const overdue = batch?.due_at && summary?.waiting > 0 && isPastDue(batch.due_at);

  return (
    <div className="modal-wrapper modal-animate-fade">
      <div className="modal-body w-full max-w-5xl animate-scale">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="card-title">{batch?.document_name || 'Group send'}</h3>
            {batch && (
              <p className="text-xs text-gray-600 mt-1">
                Sent {formatDateTime(batch.sent_at)}{batch.sender ? ` by ${batch.sender}` : ''}
                {batch.due_at && (
                  <span className={overdue ? 'text-danger font-medium' : ''}>
                    {' '}· Sign by {formatDueDate(batch.due_at)}{overdue ? ' (overdue)' : ''}
                  </span>
                )}
              </p>
            )}
          </div>
          <button type="button" onClick={onClose} className="modal-close-btn">
            <KeenIcon icon="cross" />
          </button>
        </div>

        {loading && !data ? (
          <div className="py-10 text-center text-sm text-gray-500">Loading...</div>
        ) : summary ? (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div className="flex flex-wrap gap-1.5 text-xs">
                <span className="badge badge-sm badge-outline">{summary.total} sent</span>
                <span className="badge badge-sm badge-outline badge-success">{summary.signed} signed</span>
                <span className="badge badge-sm badge-outline badge-warning">{summary.waiting} still to sign</span>
                {summary.email_failed > 0 && (
                  <span className="badge badge-sm badge-outline badge-danger">{summary.email_failed} email failed</span>
                )}
              </div>
              {summary.waiting > 0 && (
                <button type="button" className="btn btn-sm btn-primary" disabled={busy !== null} onClick={remindAll}>
                  {busy === 'all' ? 'Sending...' : `Remind everyone who hasn't signed (${summary.waiting})`}
                </button>
              )}
            </div>

            <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
              <table className="table table-auto text-sm">
                <thead>
                  <tr>
                    <th>Contact</th>
                    <th>Status</th>
                    <th>Sent</th>
                    <th>Signed</th>
                    <th>Last Reminder</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {data.documents.map((doc) => {
                    const status = recipientStatus(doc);
                    const current = doc.current_signer;
                    // First email still queued: a reminder now would send it twice.
                    const queued = current && !current.sent_at && !current.email_error;
                    return (
                      <tr key={doc.id}>
                        <td>
                          {doc.recipient ? (
                            <span className="link cursor-pointer" onClick={() => navigate(`/contacts/${doc.recipient.id}`)}>
                              {doc.recipient.name}
                            </span>
                          ) : '-'}
                          {doc.recipient?.email && <div className="text-xs text-gray-500">{doc.recipient.email}</div>}
                        </td>
                        <td>
                          <span className={`badge badge-sm badge-outline ${status.badge}`}>{status.label}</span>
                          {current?.email_error && <div className="text-xs text-danger mt-1 max-w-[260px]">{current.email_error}</div>}
                        </td>
                        <td>{formatDateTime(doc.sent_at)}</td>
                        <td>{formatDateTime(doc.completed_at)}</td>
                        <td>
                          {current?.last_reminded_at ? (
                            <>
                              {formatDateTime(current.last_reminded_at)}
                              <div className="text-xs text-gray-500">{current.reminder_count} sent</div>
                            </>
                          ) : '-'}
                        </td>
                        <td className="text-end whitespace-nowrap">
                          {doc.status === 'signed' ? (
                            <button type="button" className="btn btn-xs btn-light" onClick={() => download(doc)}>
                              <KeenIcon icon="exit-down" /> Download
                            </button>
                          ) : (
                            <button type="button" className="btn btn-xs btn-light" disabled={busy !== null || queued} title={queued ? 'The first email is still on its way' : undefined} onClick={() => remind(doc.id)}>
                              <KeenIcon icon="notification-on" /> {busy === doc.id ? 'Sending...' : 'Send reminder'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export { ESignBatchModal };
